import React, { useState, useEffect, useRef } from 'react';
import { CubeIcon, TagIcon, ChartBarIcon, ClipboardDocumentListIcon, CurrencyDollarIcon, TruckIcon, XMarkIcon, TableCellsIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';

interface AnalyticsData {
  status: string;
  value: number;
}

interface DashboardSettings {
  products: number;
  categories: number;
  tables: number;
}

interface DashboardProps {
  onPageChange?: (page: string) => void;
  onShowSubscriptionModal?: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onPageChange, onShowSubscriptionModal }) => {
  const { hasProPlan } = useAuth();
  const [analytics, setAnalytics] = useState<AnalyticsData[]>([]);
  const [settings, setSettings] = useState<DashboardSettings>({ products: 0, categories: 0, tables: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const isInitialLoad = useRef(true);

  // Ensure page starts at top when visiting this screen
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    // Set default dates to today
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    setStartDate(todayStr);
    setEndDate(todayStr);
  }, []);

  // Initial API call when dates are set
  useEffect(() => {
    if (startDate && endDate && isInitialLoad.current) {
      fetchAnalytics();
      isInitialLoad.current = false;
    }
  }, [startDate, endDate]);

  // Manual trigger only via onBlur and button click after initial load

  const fetchAnalytics = async () => {
    if (!startDate || !endDate) {
      console.log('Dashboard: Missing dates, startDate:', startDate, 'endDate:', endDate);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Convert dates to ISO strings with time
      const start = new Date(startDate + 'T00:00:00.000Z').toISOString();
      const end = new Date(endDate + 'T23:59:59.999Z').toISOString();
      
      console.log('Dashboard: Fetching analytics with dates:', { start, end });
      
      const response = await apiService.getAnalytics(start, end);
      
      console.log('Dashboard: Analytics response:', response);
      
      if (response.success) {
        console.log('Dashboard: Setting analytics data:', response.analytics);
        console.log('Dashboard: Setting settings data:', response.settings);
        setAnalytics(response.analytics);
        setSettings(response.settings);
      } else {
        console.log('Dashboard: API returned success: false');
        setError(`Failed to fetch analytics data: ${response.message || 'Unknown error'}`);
      }
    } catch (err) {
      setError('An error occurred while fetching analytics');
      console.error('Dashboard: Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = () => {
    fetchAnalytics();
  };

  // Check if user has pro plan - after all hooks are called
  if (!hasProPlan()) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md mx-auto">
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6">
            <LockClosedIcon className="w-8 h-8 text-amber-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Pro Feature</h3>
          <p className="text-gray-600 mb-6">
            Dashboard analytics and insights are available with a Pro subscription. 
            Upgrade now to access detailed analytics, order tracking, and business insights.
          </p>
          <button
            onClick={() => onShowSubscriptionModal?.()}
            className="bg-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors"
          >
            Upgrade to Pro
          </button>
        </div>
      </div>
    );
  }

  const getIconForStatus = (status: string) => {
    switch (status.toLowerCase()) {
      case 'total amount':
        return CurrencyDollarIcon;
      case 'total orders':
        return ClipboardDocumentListIcon;
      case 'delivered':
        return TruckIcon;
      case 'cancelled':
        return XMarkIcon;
      default:
        return ChartBarIcon;
    }
  };

  const formatValue = (status: string, value: number) => {
    if (status.toLowerCase() === 'total amount') {
      return `₹${value.toLocaleString()}`;
    }
    return value.toString();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm p-6 animate-pulse">
              <div className="flex items-center justify-between">
                <div>
                  <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="p-3 bg-gray-200 rounded-full">
                  <div className="h-6 w-6 bg-gray-300 rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading dashboard</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
              <div className="mt-2 text-xs text-gray-600">
                <strong>Debug Info:</strong><br/>
                Analytics: {JSON.stringify(analytics)}<br/>
                Settings: {JSON.stringify(settings)}<br/>
                Start Date: {startDate}<br/>
                End Date: {endDate}
              </div>
              <div className="mt-4">
                <button
                  onClick={fetchAnalytics}
                  className="bg-red-100 text-red-800 px-3 py-1 rounded text-sm hover:bg-red-200"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mobile Filter Toggle Button and Refresh - Always Visible */}
      <div className="block md:hidden">
        <div className="flex space-x-2">
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm text-left flex items-center justify-between hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <span className="text-sm font-medium text-gray-700">
              {startDate || endDate ? 'Date Filters Applied' : 'Date Filters'}
            </span>
            <svg
              className={`h-5 w-5 text-gray-400 transition-transform ${showMobileFilters ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            onClick={fetchAnalytics}
            disabled={loading}
            className="bg-orange-600 text-white px-4 py-3 rounded-lg shadow-sm hover:bg-orange-700 transition-colors flex items-center"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {/* Refresh */}
          </button>
        </div>
      </div>

      {/* Date Filter - Hidden on mobile unless button clicked */}
      <div className={`bg-white rounded-lg shadow-sm p-6 ${showMobileFilters ? 'block' : 'hidden md:block'}`}>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 sm:w-40">
            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              onBlur={handleDateChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <div className="flex-1 sm:w-40">
            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              onBlur={handleDateChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <div className="flex items-end space-x-2">
            <button
              onClick={handleDateChange}
              disabled={loading}
              className="flex-1 sm:flex-none px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Apply Filter'}
            </button>
            <button
              onClick={fetchAnalytics}
              disabled={loading}
              className="hidden md:flex px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Analytics Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {analytics.map((stat) => {
          const IconComponent = getIconForStatus(stat.status);
          return (
            <div key={stat.status} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.status}</p>
                  <p className="text-2xl font-semibold text-gray-900">{formatValue(stat.status, stat.value)}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <IconComponent className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Settings Summary */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Store Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center p-4 bg-orange-50 rounded-lg">
            <CubeIcon className="h-8 w-8 text-orange-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-orange-600">Products</p>
              <p className="text-2xl font-semibold text-orange-900">{settings.products}</p>
            </div>
          </div>
          <div className="flex items-center p-4 bg-green-50 rounded-lg">
            <TagIcon className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-green-600">Categories</p>
              <p className="text-2xl font-semibold text-green-900">{settings.categories}</p>
            </div>
          </div>
          <div className="flex items-center p-4 bg-purple-50 rounded-lg">
            <ChartBarIcon className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-purple-600">Tables</p>
              <p className="text-2xl font-semibold text-purple-900">{settings.tables}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            onClick={() => onPageChange?.('products')}
            className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors cursor-pointer"
          >
            <CubeIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">Add Product</p>
          </button>
          <button 
            onClick={() => onPageChange?.('categories')}
            className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors cursor-pointer"
          >
            <TagIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">Create Category</p>
          </button>
          <button 
            onClick={() => onPageChange?.('tables')}
            className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors cursor-pointer"
          >
            <TableCellsIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">Add Table</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;